import React, { useState } from 'react';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { MdClose, MdInfo, MdGavel, MdPayment, MdStars, MdStar, MdCheckCircle, MdError } from 'react-icons/md';

// Terms and Conditions Modal Component
function TermsModal({ onClose }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-card max-w-2xl w-full max-h-[85vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b bg-gray-50">
          <div className="flex items-center gap-3">
            <MdGavel className="text-2xl text-primary" />
            <h3 className="text-xl font-bold text-gray-800 font-rubik">Terms and Conditions</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-full transition-colors"
            data-testid="close-terms-button"
          >
            <MdClose className="text-xl text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(85vh-140px)]">
          {/* Refund Policy */}
          <div className="mb-6">
            <h4 className="text-lg font-bold text-gray-800 font-rubik mb-3 flex items-center gap-2">
              <span className="w-8 h-8 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-sm font-bold">!</span>
              Refund Policy
            </h4>
            <div className="bg-red-50 border border-red-200 rounded-card p-4">
              <p className="text-sm text-red-800 font-rubik font-medium">
                No refunds will be issued for any polls that are rejected due to violations of our Acceptable Use Policy. 
                Poll points spent or payments made for rejected polls will not be returned.
              </p>
            </div>
          </div>

          {/* Acceptable Use Policy */}
          <div className="mb-6">
            <h4 className="text-lg font-bold text-gray-800 font-rubik mb-3">Acceptable Use Policy</h4>
            <p className="text-sm text-gray-700 font-rubik mb-4">
              By creating a custom poll, you agree to abide by our community guidelines. All polls are automatically 
              validated and may be rejected if they contain any of the following prohibited content:
            </p>
          </div>

          {/* Prohibited Content List */}
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-card p-4 border border-gray-200">
              <h5 className="font-semibold text-gray-800 font-rubik mb-2 text-red-600">🚫 Harassment & Bullying</h5>
              <p className="text-sm text-gray-600 font-rubik">
                Content that targets, threatens, intimidates, or harasses individuals or groups.
              </p>
            </div>

            <div className="bg-gray-50 rounded-card p-4 border border-gray-200">
              <h5 className="font-semibold text-gray-800 font-rubik mb-2 text-red-600">🚫 Hate Speech & Discrimination</h5>
              <p className="text-sm text-gray-600 font-rubik">
                Content that promotes hatred, violence, or discrimination against individuals or groups.
              </p>
            </div>

            <div className="bg-gray-50 rounded-card p-4 border border-gray-200">
              <h5 className="font-semibold text-gray-800 font-rubik mb-2 text-red-600">🚫 Vulgar & Inappropriate Language</h5>
              <p className="text-sm text-gray-600 font-rubik">
                Content containing profanity, obscenities, or sexually explicit material.
              </p>
            </div>

            <div className="bg-gray-50 rounded-card p-4 border border-gray-200">
              <h5 className="font-semibold text-gray-800 font-rubik mb-2 text-red-600">🚫 Violence, Misinformation & Illegal Activities</h5>
              <p className="text-sm text-gray-600 font-rubik">
                Content promoting violence, spreading misinformation, or facilitating illegal activities.
              </p>
            </div>
          </div>

          {/* Agreement Notice */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-card">
            <p className="text-sm text-blue-800 font-rubik">
              <strong>By creating a poll, you acknowledge that:</strong>
            </p>
            <ul className="text-sm text-blue-700 font-rubik mt-2 space-y-1 list-disc list-inside">
              <li>You have read and understood these terms</li>
              <li>Your poll complies with our Acceptable Use Policy</li>
              <li>Rejected polls will not receive refunds (points or money)</li>
              <li>CIVOTE reserves the right to reject any poll</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="w-full btn-primary py-3"
            data-testid="understood-button"
          >
            I Understand
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateCustomPollModal({ tags, pollPoints, onClose, onPollCreated }) {
  const [question, setQuestion] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [autoTagging, setAutoTagging] = useState(false);
  const [durationDays, setDurationDays] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('points'); // 'points' or 'stripe'
  const [pollType, setPollType] = useState('custom'); // 'custom' or 'spotlight'
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [validationStatus, setValidationStatus] = useState(null); // null | 'validating' | 'passed' | 'failed'
  const [validationReasons, setValidationReasons] = useState([]);
  const [customDurationOptions, setCustomDurationOptions] = useState([]);
  const [spotlightDurationOptions, setSpotlightDurationOptions] = useState([]);
  const [pricingLoaded, setPricingLoaded] = useState(false);
  const [spotlightEnabled, setSpotlightEnabled] = useState(false);
  const [spotlightSurge, setSpotlightSurge] = useState(null);

  // Fetch pricing from API
  React.useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/poll-pricing`);
        const pollTypes = res.data.poll_types || [];

        // Custom poll pricing
        const customPoll = pollTypes.find(pt => pt.id === 'custom_poll');
        if (customPoll?.durations?.length > 0) {
          const options = customPoll.durations.map(d => ({
            days: d.days, points: d.price_points, price: d.price_dollars, label: d.label
          }));
          setCustomDurationOptions(options);
        } else {
          setCustomDurationOptions([
            { days: 1, points: 20, price: 2.00, label: '1 Day' },
            { days: 7, points: 100, price: 5.00, label: '7 Days' },
            { days: 30, points: 500, price: 15.00, label: '30 Days' }
          ]);
        }

        // Spotlight poll enabled check (base pricing fetched dynamically)
        const spotlightPoll = pollTypes.find(pt => pt.id === 'spotlight_poll');
        if (spotlightPoll?.enabled && spotlightPoll?.durations?.length > 0) {
          setSpotlightEnabled(true);
          // Set base pricing as initial (will be overridden by dynamic fetch)
          const options = spotlightPoll.durations.map(d => ({
            days: d.days, points: d.price_points, price: d.price_dollars, label: d.label
          }));
          setSpotlightDurationOptions(options);
        }
      } catch {
        setCustomDurationOptions([
          { days: 1, points: 20, price: 2.00, label: '1 Day' },
          { days: 7, points: 100, price: 5.00, label: '7 Days' },
          { days: 30, points: 500, price: 15.00, label: '30 Days' }
        ]);
      } finally {
        setPricingLoaded(true);
      }
    };
    fetchPricing();
  }, []);

  // Fetch dynamic spotlight pricing when poll type switches to spotlight
  React.useEffect(() => {
    if (pollType !== 'spotlight' || !spotlightEnabled) return;
    const fetchSurgePricing = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/spotlight/queue-info`);
        const data = res.data;
        setSpotlightSurge(data);
        if (data.durations?.length > 0) {
          const options = data.durations.map(d => ({
            days: d.days, points: d.price_points, price: d.price_dollars, label: d.label,
            basePrice: d.base_price_dollars, basePoints: d.base_price_points,
          }));
          setSpotlightDurationOptions(options);
        }
      } catch {
        // Keep static pricing if dynamic fetch fails
      }
    };
    fetchSurgePricing();
  }, [pollType, spotlightEnabled]);

  // Set default duration when pricing loads or poll type changes
  React.useEffect(() => {
    const opts = pollType === 'spotlight' ? spotlightDurationOptions : customDurationOptions;
    if (opts.length > 0 && !opts.find(d => d.days === durationDays)) {
      setDurationDays(opts[0].days);
    }
  }, [pollType, customDurationOptions, spotlightDurationOptions, durationDays]);

  const durationOptions = pollType === 'spotlight' ? spotlightDurationOptions : customDurationOptions;
  const selectedDuration = durationOptions.find(d => d.days === durationDays) || durationOptions[0];
  const hasEnoughPoints = selectedDuration ? pollPoints >= selectedDuration.points : false;

  // Auto-select payment method based on points availability
  React.useEffect(() => {
    if (!hasEnoughPoints && paymentMethod === 'points') {
      setPaymentMethod('stripe');
    }
  }, [durationDays, hasEnoughPoints, paymentMethod]);

  // Auto-tag when question changes (debounced)
  React.useEffect(() => {
    if (question.trim().length < 10) {
      setSelectedTags([]);
      return;
    }
    const timer = setTimeout(async () => {
      setAutoTagging(true);
      try {
        const res = await axios.post(`${API_URL}/api/auto-tag`, { question: question.trim() });
        setSelectedTags(res.data.tags || []);
      } catch {
        setSelectedTags([]);
      } finally {
        setAutoTagging(false);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [question]);

  // AI validation when question changes (debounced, longer delay)
  React.useEffect(() => {
    if (question.trim().length < 10) {
      setValidationStatus(null);
      setValidationReasons([]);
      return;
    }
    setValidationStatus('validating');
    const timer = setTimeout(async () => {
      try {
        const res = await axios.post(`${API_URL}/api/polls/validate`, { question: question.trim() });
        if (res.data.valid) {
          setValidationStatus('passed');
          setValidationReasons([]);
        } else {
          setValidationStatus('failed');
          setValidationReasons(res.data.reasons || ['Your question did not pass validation']);
        }
      } catch {
        setValidationStatus(null);
        setValidationReasons([]);
      }
    }, 1200);
    return () => clearTimeout(timer);
  }, [question]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!question.trim()) {
      setError('Please enter a question');
      return;
    }

    if (!agreedToTerms) {
      setError('Please agree to the Terms and Conditions');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      if (paymentMethod === 'points') {
        // Use points - existing flow
        const response = await axios.post(`${API_URL}/api/custom-polls`, {
          question: question.trim(),
          tags: selectedTags,
          duration_days: durationDays,
          poll_type: pollType,
        });

        if (response.data && response.data.poll) {
          onPollCreated && onPollCreated(response.data.poll);
          onClose();
        }
      } else {
        // Use Stripe - redirect to checkout
        const response = await axios.post(`${API_URL}/api/payments/create-checkout`, {
          duration_days: durationDays,
          question: question.trim(),
          tags: selectedTags,
          origin_url: window.location.origin,
          poll_type: pollType,
        });

        if (response.data.checkout_url) {
          window.location.href = response.data.checkout_url;
        } else {
          setError('Failed to create payment session. Please try again.');
          setSubmitting(false);
        }
      }
    } catch (err) {
      console.error('Error creating poll:', err);
      setError(err.response?.data?.detail || 'Failed to create poll. Please try again.');
      setSubmitting(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-card max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white z-10">
            <h2 className="text-xl font-bold text-gray-800 font-rubik">
              {pollType === 'spotlight' ? 'Create Spotlight Poll' : 'Create Custom Poll'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              data-testid="close-modal-button"
            >
              <MdClose className="text-2xl text-gray-500" />
            </button>
          </div>

          {/* Disclaimer */}
          <div className="bg-blue-50 border-b border-blue-200 px-6 py-4">
            <div className="flex items-start gap-3">
              <MdInfo className="text-xl text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800 font-rubik">
                <p className="font-semibold mb-1">Important:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Your question must be phrased as a Yes/No question.</li>
                  <li>Questions are automatically checked for profanity, personal attacks, and propaganda.</li>
                  <li><strong>You cannot edit your question once submitted</strong> to protect the integrity of poll results.</li>
                  <li>Once payment is complete, your poll goes live immediately.</li>
                </ul>
                <p className="mt-2 text-xs text-blue-600">Example: &quot;Should public transportation be free for students?&quot;</p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Loading pricing state */}
            {!pricingLoaded ? (
              <div className="text-center py-6 text-gray-400 text-sm font-rubik">Loading pricing...</div>
            ) : (
            <>
            {/* Poll Type Selection */}
            {spotlightEnabled && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-rubik">
                  Poll Type
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setPollType('custom')}
                    className={`p-3 rounded-card border-2 text-left transition-all ${
                      pollType === 'custom'
                        ? 'border-primary bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="poll-type-custom"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <MdStars className={`text-lg ${pollType === 'custom' ? 'text-primary' : 'text-gray-400'}`} />
                      <span className="font-semibold text-gray-800 font-rubik text-sm">Custom Poll</span>
                    </div>
                    <p className="text-xs text-gray-500 font-rubik">Standard community poll</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPollType('spotlight')}
                    className={`p-3 rounded-card border-2 text-left transition-all ${
                      pollType === 'spotlight'
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="poll-type-spotlight"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <MdStar className={`text-lg ${pollType === 'spotlight' ? 'text-amber-500' : 'text-gray-400'}`} />
                      <span className="font-semibold text-gray-800 font-rubik text-sm">Spotlight Poll</span>
                    </div>
                    <p className="text-xs text-gray-500 font-rubik">Priority community poll</p>
                  </button>
                </div>
                {pollType === 'spotlight' && (
                  <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                    {spotlightSurge && spotlightSurge.queue_depth > 0 ? (
                      <div>
                        <p className="text-xs text-amber-800 font-rubik font-medium">
                          Current queue: {spotlightSurge.queue_depth} poll{spotlightSurge.queue_depth !== 1 ? 's' : ''} — ~{spotlightSurge.wait_hours < 1 ? '<1' : Math.round(spotlightSurge.wait_hours)}h estimated wait
                        </p>
                        <p className="text-xs text-amber-600 font-rubik mt-0.5">
                          Prices are {spotlightSurge.multiplier}x base due to demand.
                        </p>
                      </div>
                    ) : (
                      <p className="text-xs text-amber-800 font-rubik">
                        No queue — your spotlight poll will be shown immediately at base price.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 font-rubik">
                Your Question <span className="text-red-500">*</span>
              </label>
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Enter your yes/no poll question..."
                className="w-full px-4 py-3 border border-gray-300 rounded-card focus:ring-2 focus:ring-primary focus:border-transparent font-rubik resize-none"
                rows={3}
                maxLength={250}
                data-testid="question-input"
              />
              <p className="text-xs text-gray-500 mt-1 font-rubik">{question.length}/250 characters</p>
              {/* AI Validation Status */}
              {validationStatus === 'validating' && (
                <div className="flex items-center gap-2 mt-2 text-sm text-gray-500 font-rubik" data-testid="validation-checking">
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-primary" />
                  Checking your question...
                </div>
              )}
              {validationStatus === 'passed' && (
                <div className="flex items-center gap-2 mt-2 text-sm text-green-600 font-rubik" data-testid="validation-passed">
                  <MdCheckCircle className="text-base" />
                  Question approved — ready to submit!
                </div>
              )}
              {validationStatus === 'failed' && (
                <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="validation-failed">
                  <div className="flex items-center gap-2 text-sm text-red-700 font-rubik font-medium">
                    <MdError className="text-base flex-shrink-0" />
                    Your question needs adjustment:
                  </div>
                  <ul className="mt-1 text-xs text-red-600 font-rubik list-disc list-inside space-y-0.5">
                    {validationReasons.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                </div>
              )}
            </div>

            {/* Auto-assigned Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 font-rubik">
                Tags
                <span className="text-xs text-gray-400 font-normal ml-1">(auto-assigned based on your question)</span>
              </label>
              {autoTagging ? (
                <div className="flex items-center gap-2 p-3 border border-gray-200 rounded-card">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                  <span className="text-sm text-gray-500 font-rubik">Analyzing question...</span>
                </div>
              ) : selectedTags.length > 0 ? (
                <div className="flex flex-wrap gap-2 p-3 border border-gray-200 rounded-card bg-gray-50" data-testid="auto-tags-display">
                  {selectedTags.map(tagId => {
                    const tag = tags.find(t => t.tagId === tagId);
                    if (!tag) return null;
                    return (
                      <span
                        key={tagId}
                        className="px-3 py-1.5 rounded-full text-sm font-medium font-rubik text-white"
                        style={{ backgroundColor: tag.color }}
                        data-testid={`auto-tag-${tagId}`}
                      >
                        {tag.name}
                      </span>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-gray-400 font-rubik p-3 border border-gray-200 rounded-card">
                  {question.trim().length < 10 ? 'Type your question to auto-assign tags' : 'No matching tags found'}
                </p>
              )}
            </div>

            {/* Duration Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 font-rubik">
                Poll Duration
              </label>
              <div className="grid grid-cols-3 gap-3">
                {durationOptions.map(option => (
                  <button
                    key={option.days}
                    type="button"
                    onClick={() => setDurationDays(option.days)}
                    className={`p-3 rounded-card border-2 text-center transition-all ${
                      durationDays === option.days
                        ? 'border-primary bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`duration-${option.days}`}
                  >
                    <div className="text-base font-bold text-gray-800 font-rubik">{option.label}</div>
                    <div className="text-xs text-gray-500 font-rubik mt-1">
                      {option.points} pts / {formatPrice(option.price)}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Payment Method Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 font-rubik">
                Payment Method
              </label>
              <div className="grid grid-cols-2 gap-3">
                {/* Points Option */}
                <button
                  type="button"
                  onClick={() => hasEnoughPoints && setPaymentMethod('points')}
                  disabled={!hasEnoughPoints}
                  className={`p-4 rounded-card border-2 text-left transition-all ${
                    paymentMethod === 'points'
                      ? 'border-tertiary bg-orange-50'
                      : hasEnoughPoints 
                        ? 'border-gray-200 hover:border-gray-300' 
                        : 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                  }`}
                  data-testid="payment-method-points"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <MdStars className={`text-xl ${paymentMethod === 'points' ? 'text-tertiary' : 'text-gray-400'}`} />
                    <span className="font-semibold text-gray-800 font-rubik">Use Points</span>
                  </div>
                  <div className={`text-lg font-bold font-rubik ${hasEnoughPoints ? 'text-tertiary' : 'text-gray-400'}`}>
                    {selectedDuration.points} pts
                  </div>
                  <div className="text-xs text-gray-500 font-rubik mt-1">
                    You have: {pollPoints} pts
                  </div>
                  {!hasEnoughPoints && (
                    <div className="text-xs text-red-500 font-rubik mt-1">
                      Need {selectedDuration.points - pollPoints} more pts
                    </div>
                  )}
                </button>

                {/* Stripe Option */}
                <button
                  type="button"
                  onClick={() => setPaymentMethod('stripe')}
                  className={`p-4 rounded-card border-2 text-left transition-all ${
                    paymentMethod === 'stripe'
                      ? 'border-primary bg-green-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="payment-method-stripe"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <MdPayment className={`text-xl ${paymentMethod === 'stripe' ? 'text-primary' : 'text-gray-400'}`} />
                    <span className="font-semibold text-gray-800 font-rubik">Pay with Card</span>
                  </div>
                  <div className={`text-lg font-bold font-rubik ${paymentMethod === 'stripe' ? 'text-primary' : 'text-gray-600'}`}>
                    {formatPrice(selectedDuration.price)}
                  </div>
                  <div className="text-xs text-gray-500 font-rubik mt-1">
                    Secure checkout
                  </div>
                </button>
              </div>
            </div>

            {/* Summary */}
            <div className={`p-4 rounded-card border ${
              paymentMethod === 'points' 
                ? 'bg-orange-50 border-orange-200' 
                : 'bg-green-50 border-green-200'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700 font-rubik">Selected Plan</p>
                  <p className="text-lg font-bold text-gray-800 font-rubik">{selectedDuration.label} Poll</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-700 font-rubik">Total</p>
                  {paymentMethod === 'points' ? (
                    <p className="text-2xl font-bold text-tertiary font-rubik">{selectedDuration.points} pts</p>
                  ) : (
                    <p className="text-2xl font-bold text-primary font-rubik">{formatPrice(selectedDuration.price)}</p>
                  )}
                </div>
              </div>
              {paymentMethod === 'points' && (
                <div className="mt-2 pt-2 border-t border-orange-200 text-sm text-gray-600 font-rubik">
                  Remaining after purchase: <strong>{pollPoints - selectedDuration.points} pts</strong>
                </div>
              )}
            </div>

            {/* Terms and Conditions Checkbox */}
            <div className="bg-amber-50 border border-amber-200 rounded-card p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1 h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                  data-testid="terms-checkbox"
                />
                <span className="text-sm text-gray-700 font-rubik">
                  I have read and agree to the{' '}
                  <button
                    type="button"
                    onClick={() => setShowTermsModal(true)}
                    className="text-primary hover:text-tertiary font-semibold underline"
                    data-testid="terms-link"
                  >
                    Terms and Conditions
                  </button>
                  , including the Acceptable Use Policy and refund policy.
                </span>
              </label>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-card p-4 text-red-700 text-sm font-rubik">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <div className="flex gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 btn-secondary py-3"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !question.trim() || !agreedToTerms || validationStatus !== 'passed' || (paymentMethod === 'points' && !hasEnoughPoints)}
                className={`flex-1 py-3 rounded-card font-semibold font-rubik transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
                  paymentMethod === 'points' 
                    ? 'bg-tertiary hover:bg-orange-600 text-white' 
                    : 'bg-primary hover:bg-green-700 text-white'
                }`}
                data-testid="submit-poll-button"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Processing...
                  </>
                ) : paymentMethod === 'points' ? (
                  <>
                    <MdStars className="text-lg" />
                    Use {selectedDuration.points} Points
                  </>
                ) : (
                  <>
                    <MdPayment className="text-lg" />
                    Pay {formatPrice(selectedDuration.price)}
                  </>
                )}
              </button>
            </div>

            {/* Footer Note */}
            {paymentMethod === 'stripe' && (
              <div className="text-center">
                <p className="text-xs text-gray-400 font-rubik">
                  Secure payment powered by Stripe
                </p>
              </div>
            )}
            </>
            )}
          </form>
        </div>
      </div>

      {/* Terms Modal */}
      {showTermsModal && <TermsModal onClose={() => setShowTermsModal(false)} />}
    </>
  );
}

export default CreateCustomPollModal;
